import * as LabelPrimitives from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import { createFormHook, createFormHookContexts } from '@tanstack/react-form'
import getSymbolFromCurrency from 'currency-symbol-map'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import React, { ReactNode, forwardRef, useEffect, useId, useState } from 'react'

import { Button } from '@/components/base/button'
import { Calendar } from '@/components/base/calendar'
import { Combobox, ComboboxOption, ComboboxProps } from '@/components/base/combobox'
import { Container } from '@/components/base/container'
import { CurrencyInput } from '@/components/base/currency-input'
import { Hint as HintComponent } from '@/components/base/hint'
import { Input } from '@/components/base/input'
import { Label as LabelComponent } from '@/components/base/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/base/popover'
import { Select } from '@/components/base/select'
import { Switch } from '@/components/base/switch'
import { Text } from '@/components/base/text'
import { Textarea } from '@/components/base/textarea'
import { Tooltip } from '@/components/base/tooltip'
import { useI18n } from '@/i18n/i18n-react'
import { InformationCircleSolid } from '@/icons/information-circle-solid'
import { Spinner } from '@/icons/spinner'
import { clx } from '@/utils/clx'
import { fromPrecision, PrecisionAmount, toPrecision } from '@/utils/currency'

const { fieldContext, useFieldContext, formContext, useFormContext } = createFormHookContexts()

export const FormFieldContainer = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => {
		return <div ref={ref} className={clx('flex flex-col space-y-2', className)} {...props} />
	}
)

export const FormLabel = forwardRef<
	React.ElementRef<typeof LabelPrimitives.Root>,
	React.ComponentPropsWithoutRef<typeof LabelPrimitives.Root> & {
		optional?: boolean
		tooltip?: ReactNode
		icon?: ReactNode
	}
>(({ className, optional = false, tooltip, icon, ...props }, ref) => {
	const { L } = useI18n()

	return (
		<div className='flex items-center gap-x-1'>
			<LabelComponent
				// id={formLabelId}
				ref={ref}
				className={clx(className)}
				size='small'
				weight='plus'
				{...props}
			/>
			{tooltip && (
				<Tooltip content={tooltip}>
					<InformationCircleSolid className='text-ui-fg-muted' />
				</Tooltip>
			)}
			{icon}
			{optional && (
				<Text size='small' leading='compact' className='text-ui-fg-muted'>
					({L.General.Optional()})
				</Text>
			)}
		</div>
	)
})

export const FormHint = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
	({ className, ...props }, ref) => {
		return <HintComponent ref={ref} className={className} {...props} />
	}
)

export const FormErrorMessage = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
	({ className, children, ...props }, ref) => {
		const field = useFieldContext()
		const errors = field.state.meta.errors ?? []
		if (errors.length === 0) {
			return null
		}

		return (
			<>
				{errors.map((error, index) => (
					<HintComponent key={index} ref={ref} className={className} variant='error' {...props}>
						{error}
					</HintComponent>
				))}
			</>
		)
	}
)

const TextField = ({
	label,
	optional,
	type = 'text',
	...props
}: {
	label?: string
	optional?: boolean
	type?: 'text' | 'password' | 'email' | 'number' | 'date'
	min?: number
	max?: number
} & React.ComponentPropsWithoutRef<typeof Input>) => {
	const field = useFieldContext<string>()
	const id = useId()
	return (
		<FormFieldContainer>
			<FormLabel htmlFor={id} optional={optional}>
				{label}
			</FormLabel>
			<Slot id={id}>
				<Input
					type={type}
					value={field.state.value}
					{...props}
					onChange={e => field.handleChange(e.target.value)}
					onBlur={e => field.handleBlur()}
				/>
			</Slot>
			<FormErrorMessage />
		</FormFieldContainer>
	)
}

const NumberField = ({
	label,
	optional,
	...props
}: {
	label?: string
	optional?: boolean
} & React.ComponentPropsWithoutRef<typeof Input>) => {
	const field = useFieldContext<number | undefined>()
	const id = useId()
	return (
		<FormFieldContainer>
			<FormLabel htmlFor={id} optional={optional}>
				{label}
			</FormLabel>
			<Slot id={id}>
				<Input
					type='number'
					value={field.state.value ?? ''}
					{...props}
					onChange={e => field.handleChange(e.target.value === '' ? undefined : Number(e.target.value))}
					onBlur={e => field.handleBlur()}
				/>
			</Slot>
			<FormErrorMessage />
		</FormFieldContainer>
	)
}

const TextAreaField = ({ label, optional, rows = 6 }: { label: string; optional?: boolean; rows?: number }) => {
	const field = useFieldContext<string>()
	const id = useId()
	return (
		<FormFieldContainer>
			<FormLabel htmlFor={id} optional={optional}>
				{label}
			</FormLabel>
			<Slot id={id}>
				<Textarea
					rows={rows}
					value={field.state.value}
					onChange={e => field.handleChange(e.target.value)}
					onBlur={e => field.handleBlur()}
				/>
			</Slot>
			<FormErrorMessage />
		</FormFieldContainer>
	)
}

const BooleanField = ({ label, hint }: { label: string; hint?: string }) => {
	const field = useFieldContext<boolean>()
	const id = useId()
	return (
		<FormFieldContainer>
			<FormLabel htmlFor={id}>{label}</FormLabel>
			<Slot id={id}>
				<Switch
					checked={field.state.value}
					onCheckedChange={field.handleChange}
					onBlur={() => field.handleBlur()}
				/>
			</Slot>
			{hint && <FormHint>{hint}</FormHint>}
			<FormErrorMessage />
		</FormFieldContainer>
	)
}

const DateField = ({ label, optional, ...props }: { label?: string; optional?: boolean }) => {
	const field = useFieldContext<number>()
	const { L } = useI18n()
	const id = useId()
	const [isOpen, setIsOpen] = useState(false)

	const date = field.state.value ? new Date(field.state.value) : undefined

	return (
		<FormFieldContainer>
			<FormLabel htmlFor={id} optional={optional}>
				{label}
			</FormLabel>
			<Slot id={id}>
				<Popover open={isOpen} onOpenChange={setIsOpen} modal>
					<PopoverTrigger asChild>
						<Button
							id={id}
							variant='secondary'
							className={clx(
								'w-full justify-start text-left font-normal',
								!date && 'text-muted-foreground'
							)}
						>
							<CalendarIcon className='mr-2 h-4 w-4' />
							{date ? format(date, 'PPP') : <span>{L.General.PickDate()}</span>}
						</Button>
					</PopoverTrigger>
					<PopoverContent className='pointer-events-auto w-auto p-0' align='start'>
						<Calendar
							mode='single'
							selected={date}
							onSelect={selected => {
								field.handleChange(selected ? selected.getTime() : 0)
								setIsOpen(false)
							}}
						/>
					</PopoverContent>
				</Popover>
			</Slot>
			<FormErrorMessage />
		</FormFieldContainer>
	)
}

/**
 * Radix UI's `Select.Item` doesn't allow an empty string value (it's reserved for
 * clearing the selection), so options with a `''` value (e.g. "None") are mapped
 * to this sentinel internally and mapped back on change.
 */
const SELECT_EMPTY_VALUE = '__EMPTY__'

const SelectField = ({
	label,
	options,
	optionsLoading
}: {
	label?: string
	options: { label: string; value: string }[]
	optionsLoading?: boolean
}) => {
	const { L } = useI18n()
	const field = useFieldContext<string>()
	const id = useId()
	return (
		<FormFieldContainer>
			<FormLabel htmlFor={id}>{label}</FormLabel>
			<Slot id={id}>
				<Select
					onValueChange={value => field.handleChange(value === SELECT_EMPTY_VALUE ? '' : value)}
					value={field.state.value === '' ? SELECT_EMPTY_VALUE : field.state.value}
					disabled={optionsLoading}
				>
					<Select.Trigger>
						{optionsLoading ? (
							<div className='flex items-center gap-x-2'>
								<Spinner className='animate-spin' />
								{L.General.Loading()}
							</div>
						) : (
							<Select.Value />
						)}
					</Select.Trigger>
					<Select.Content>
						{optionsLoading ? (
							<Select.Item key='loading' value='loading'>
								{L.General.Loading()}
							</Select.Item>
						) : (
							options.map(option => (
								<Select.Item
									key={option.value}
									value={option.value === '' ? SELECT_EMPTY_VALUE : option.value}
								>
									{option.label}
								</Select.Item>
							))
						)}
					</Select.Content>
				</Select>
			</Slot>
		</FormFieldContainer>
	)
}

const ComboboxField = ({
	label,
	optional,
	...props
}: {
	label: string
	optional?: boolean
	options: ComboboxOption[]
} & Omit<Partial<ComboboxProps>, 'optional'>) => {
	const field = useFieldContext<string[]>()
	const id = useId()
	return (
		<FormFieldContainer>
			<FormLabel htmlFor={id} optional={optional}>
				{label}
			</FormLabel>
			<Slot id={id}>
				<Combobox
					{...props}
					value={field.state.value}
					onChange={value => field.handleChange((value as string[]) ?? [])}
					onBlur={() => field.handleBlur()}
				/>
			</Slot>
		</FormFieldContainer>
	)
}

/**
 * Single-select variant of ComboboxField.
 * Uses a string field context instead of string[] so that the underlying
 * Combobox renders in single-select mode (shows the selected label when closed).
 */
const SingleComboboxField = ({
	label,
	optional,
	...props
}: {
	label: string
	optional?: boolean
	options: ComboboxOption[]
} & Omit<Partial<ComboboxProps<string>>, 'optional'>) => {
	const field = useFieldContext<string>()
	const id = useId()
	return (
		<FormFieldContainer>
			<FormLabel htmlFor={id} optional={optional}>
				{label}
			</FormLabel>
			<Slot id={id}>
				<Combobox
					{...props}
					value={field.state.value}
					onChange={value => field.handleChange((value as string) ?? '')}
					onBlur={() => field.handleBlur()}
				/>
			</Slot>
		</FormFieldContainer>
	)
}

const CurrencyField = ({
	label,
	currencyCode,
	disabled
}: {
	label: string
	currencyCode: string
	disabled?: boolean
}) => {
	const field = useFieldContext<PrecisionAmount | null>()
	const id = useId()
	const [inputValue, setInputValue] = useState(field.state.value ? fromPrecision(field.state.value).toString() : '')

	// Reflect programmatic value changes (e.g. a parent setting/clearing the amount) in the input.
	// User typing keeps `inputValue` and the field value numerically in sync, so this no-ops then.
	useEffect(() => {
		const externalFloat = field.state.value ? fromPrecision(field.state.value) : null
		const currentFloat = inputValue === '' ? null : parseFloat(inputValue)
		if (externalFloat !== currentFloat) {
			setInputValue(externalFloat === null ? '' : externalFloat.toString())
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [field.state.value])

	return (
		<FormFieldContainer>
			<FormLabel htmlFor={id}>{label}</FormLabel>
			<Slot id={id}>
				<CurrencyInput
					min={0}
					disabled={disabled}
					onBlur={() => field.handleBlur()}
					onValueChange={(value, name, values) => {
						value = value ?? ''
						setInputValue(value)

						if (values?.float === null) {
							field.handleChange(null)
							return
						}
						const floatValue = values?.float ?? 0

						if (!isNaN(floatValue)) {
							field.handleChange(toPrecision(floatValue, 2))
						}
					}}
					code={currencyCode}
					symbol={getSymbolFromCurrency(currencyCode) ?? currencyCode}
					value={inputValue}
				/>
			</Slot>
			<FormErrorMessage />
		</FormFieldContainer>
	)
}

function CreateButton({ label }: { label?: string }) {
	const { L } = useI18n()
	const form = useFormContext()
	return (
		<form.Subscribe
			selector={state => ({
				isSubmitting: state.isSubmitting,
				isValid: state.isValid,
				isTouched: state.isTouched
			})}
		>
			{({ isSubmitting, isValid, isTouched }) => {
				return (
					<Button
						size='small'
						type='submit'
						isLoading={isSubmitting}
						disabled={!isValid || !isTouched}
						onClick={() => form.handleSubmit()}
					>
						{label ?? L.General.Create()}
					</Button>
				)
			}}
		</form.Subscribe>
	)
}

function SaveButton({ label, className }: { label?: string; className?: string }) {
	const { L } = useI18n()
	const form = useFormContext()
	return (
		<form.Subscribe selector={state => ({ isSubmitting: state.isSubmitting, isValid: state.isValid })}>
			{({ isSubmitting, isValid }) => {
				return (
					<Button
						size='small'
						type='submit'
						isLoading={isSubmitting}
						disabled={!isValid}
						onClick={() => form.handleSubmit()}
						className={className}
					>
						{label ?? L.General.Save()}
					</Button>
				)
			}}
		</form.Subscribe>
	)
}

const { useAppForm, withForm } = createFormHook({
	fieldComponents: {
		TextField,
		NumberField,
		SelectField,
		ComboboxField,
		SingleComboboxField,
		CurrencyField,
		BooleanField,
		TextAreaField,
		DateField
	},
	formComponents: {
		SaveButton,
		CreateButton
	},
	fieldContext,
	formContext
})

export { useAppForm, withForm }

export const Tester = () => {
	const form = useAppForm({
		defaultValues: {
			firstName: 'John',
			lastName: 'Doe',
			status: 'DRAFT',
			tags: ['tag1', 'tag2'],
			amount: 100,
			automaticTaxes: true
		}
	})

	return (
		<Container className='max-w-[400px] space-y-4 px-6 py-4'>
			<p>Test form</p>
			<form.AppField
				name='firstName'
				validators={{
					onChange: ({ value }) => {
						if (value.length < 3) {
							return 'First name must be at least 3 characters'
						}
					}
				}}
				children={field => <field.TextField label='First Name' />}
			/>
			<form.AppField
				name='lastName'
				validators={{
					onChange: ({ value }) => {
						console.log('onBlur', value)
						if (value.length < 3) {
							return 'Last name must be at least 3 characters'
						}
					}
				}}
				children={field => <field.TextField label='Last Name' />}
			/>
			<form.AppField
				name='status'
				children={field => (
					<field.SelectField
						label='Status'
						options={[
							{ label: 'Draft', value: 'DRAFT' },
							{ label: 'Published', value: 'PUBLISHED' },
							{ label: 'Proposed', value: 'PROPOSED' },
							{ label: 'Rejected', value: 'REJECTED' }
						]}
					/>
				)}
			/>
			<form.AppField
				name='tags'
				children={field => (
					<field.ComboboxField
						label='Tags'
						multiple
						options={[
							{ label: 'Tag 1', value: 'tag1' },
							{ label: 'Tag 2', value: 'tag2' },
							{ label: 'Tag 3', value: 'tag3' }
						]}
					/>
				)}
			/>
			{/* <form.AppField
				name='amount'
				children={field => <field.CurrencyField label='Amount' currencyCode='USD' />}
			/> */}
			<form.AppField
				name='automaticTaxes'
				children={field => <field.BooleanField label='Automatic Taxes' hint='asdasdasd' />}
			/>
			<form.AppForm>
				<form.SaveButton />
			</form.AppForm>
		</Container>
	)
}

export const FormContainer = ({ children, className }: { children: ReactNode; className?: string }) => {
	return <div className={clx('flex w-full flex-col gap-y-6', className)}>{children}</div>
}

export const FormFieldGroup = ({ children }: { children: ReactNode }) => {
	return <div className='flex flex-col gap-y-2'>{children}</div>
}
